import { Box, Button, Dialog, DialogContent, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import React from 'react';

import { DialogChromeFooter, DialogChromeHeader, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';
import { ReviewForm } from 'interfaces/reviewForm';
import { Study } from 'interfaces/study';
import { ResultsExportType } from 'services/export/exportTypes';
import { useResultsExportUrlState } from 'services/export/useResultsExportUrlState';
import { useStudyExportController } from 'services/export/useStudyExportController';
import { getResultsExportChoiceAvailability } from './exportChoiceAvailability';
import { resultsExportChoices } from './exportChoices';
import { FeaturesExportForm } from './FeaturesExportForm';
import { IntermediateOutputsExportForm } from './IntermediateOutputsExportForm';
import { MetadataExportForm } from './MetadataExportForm';
import { NormalizationExportForm } from './NormalizationExportForm';
import { OriginalFilesExportForm } from './OriginalFilesExportForm';
import { PolygonsExportForm } from './PolygonsExportForm';
import { PreparedResultsExportForm } from './PreparedResultsExportForm';
import { useResultsExportContext } from './useResultsExportContext';

interface ResultsExportDialogProps {
  study?: Study;
  forms?: ReviewForm[];
  skipPreloadingStudyStains?: boolean;
  onClose: () => void;
  fixedType?: ResultsExportType;
}

// Chooser first, then one focused form. Each form calls its existing export owner.
// Selected type, stage and form drafts live in the URL; the host clears them on close.
const ResultsExportDialog: React.FC<ResultsExportDialogProps> = ({
  study,
  forms,
  skipPreloadingStudyStains,
  onClose,
  fixedType,
}) => {
  const context = useResultsExportContext(study);
  const { startExport, exportProgressDialog, isExportRunning } = useStudyExportController();
  const { query, update, clearDraft } = useResultsExportUrlState();

  const availability = (type: ResultsExportType) => getResultsExportChoiceAvailability(type, context);

  const selectedType =
    fixedType ?? resultsExportChoices.find((choice) => choice.type === query.resultsExportType)?.type;
  const stage: 'choose' | 'configure' =
    (fixedType || query.resultsExportStage === 'configure') && selectedType && !availability(selectedType).disabled
      ? 'configure'
      : 'choose';
  // The form stays mounted behind Back so its non-URL draft survives; choosing another type replaces it.
  const [keptFormType, setKeptFormType] = React.useState<ResultsExportType | undefined>();
  const formType = stage === 'configure' ? selectedType : keptFormType;

  const formContainerRef = React.useRef<HTMLDivElement>(null);
  const choiceInputs = React.useRef<Partial<Record<ResultsExportType, HTMLInputElement | null>>>({});

  React.useEffect(() => {
    if (stage === 'configure') {
      formContainerRef.current?.querySelector<HTMLElement>('input:not(:disabled), button:not(:disabled)')?.focus();
    } else if (selectedType) {
      choiceInputs.current[selectedType]?.focus();
    }
    // Focus moves only when the stage changes, not on every selection.
  }, [stage]);

  const formChoice = resultsExportChoices.find((choice) => choice.type === formType);

  const goBack = () => {
    if (fixedType) {
      onClose();
      return;
    }
    setKeptFormType(formType);
    update({ resultsExportStage: undefined });
  };
  const handleContinue = () => {
    if (!selectedType || availability(selectedType).disabled) return;
    if (formType && formType !== selectedType) clearDraft(formType);
    setKeptFormType(selectedType);
    update({ resultsExportStage: 'configure' });
  };

  const renderForm = (type: ResultsExportType) => {
    switch (type) {
      case 'published':
      case 'bundle':
        return (
          <PreparedResultsExportForm
            context={context}
            wholeStudyPublishedOnly={type === 'published'}
            onBack={goBack}
            onClose={onClose}
          />
        );
      case 'original':
        return (
          <OriginalFilesExportForm
            context={context}
            startExport={startExport}
            isExportRunning={isExportRunning}
            onBack={goBack}
          />
        );
      case 'intermediate':
        return <IntermediateOutputsExportForm context={context} onBack={goBack} />;
      case 'features':
        return (
          <FeaturesExportForm
            context={context}
            skipPreloadingStudyStains={skipPreloadingStudyStains}
            startExport={startExport}
            isExportRunning={isExportRunning}
            onBack={goBack}
          />
        );
      case 'metadata':
        return (
          <MetadataExportForm
            context={context}
            forms={forms}
            skipPreloadingStudyStains={skipPreloadingStudyStains}
            startExport={startExport}
            isExportRunning={isExportRunning}
            onBack={goBack}
          />
        );
      case 'polygons':
        return (
          <PolygonsExportForm
            context={context}
            startExport={startExport}
            isExportRunning={isExportRunning}
            onBack={goBack}
          />
        );
      case 'normalization':
        return (
          <NormalizationExportForm
            context={context}
            startExport={startExport}
            isExportRunning={isExportRunning}
            onBack={goBack}
            backLabel={fixedType ? 'Close' : undefined}
          />
        );
      default:
        return null;
    }
  };

  const isConfiguring = stage === 'configure' && Boolean(formChoice);
  const expansionNote = formType ? availability(formType).note : undefined;

  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth="md"
        onClose={() => onClose()}
        onClick={(event) => event.stopPropagation()}
        aria-labelledby="results-export-title"
      >
        <DialogChromeHeader
          titleId="results-export-title"
          title={isConfiguring ? formChoice?.formTitle : 'Export data'}
          subtitle={isConfiguring ? expansionNote : undefined}
          onClose={() => onClose()}
          closeButtonAriaLabel="Close export"
        />
        {stage === 'choose' && (
          <>
            <DialogContent dividers>
              <Typography component="p" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose what to export.
              </Typography>
              <RadioGroup
                aria-label="Export type"
                value={selectedType ?? ''}
                onChange={(event) => update({ resultsExportType: event.target.value })}
              >
                {resultsExportChoices.map((choice) => {
                  const { disabled, note } = availability(choice.type);
                  return (
                    <FormControlLabel
                      key={choice.type}
                      value={choice.type}
                      disabled={disabled}
                      sx={{ alignItems: 'flex-start', mb: 1 }}
                      control={
                        <Radio
                          inputRef={(input) => {
                            choiceInputs.current[choice.type] = input;
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" component="span" sx={{ display: 'block' }}>
                            {choice.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                            {choice.description}
                          </Typography>
                          {note && (
                            <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                              {note}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </DialogContent>
            <DialogChromeFooter>
              <Typography variant="body2" color="text.secondary" role="status" sx={{ mr: 'auto' }}>
                {selectedType ? '' : 'Select an export type to continue.'}
              </Typography>
              <Button color="inherit" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={!selectedType || availability(selectedType).disabled}
                onClick={handleContinue}
                sx={dialogPrimaryActionSx}
              >
                Continue
              </Button>
            </DialogChromeFooter>
          </>
        )}
        {formType && (
          <Box ref={formContainerRef} sx={{ display: stage === 'configure' ? 'contents' : 'none' }}>
            {renderForm(formType)}
          </Box>
        )}
      </Dialog>
      {exportProgressDialog}
    </>
  );
};

export default ResultsExportDialog;
